import { FunctionComponent, ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { faArrowUpRightFromSquare } from "@fortawesome/pro-regular-svg-icons";

import { Icon } from "@/components";
import { Toggle } from "@/components/Toggle/Toggle";
import { classNames } from "@/utils/classNames";

import { MatrixCell, MatrixCellActions } from "./ScoreMatrix/MatrixCell";
import { ScoringOption } from "./ScoreMatrix/matrixModel";
import { CellAssignment, PersonaView, SegmentView } from "./scoreConfigView";

export interface PersonaSegmentRow {
	segment: SegmentView;
	persona: PersonaView;
	linked: boolean;
	assignment: CellAssignment | null;
}

interface PersonaSegmentTableProps extends MatrixCellActions {
	rows: PersonaSegmentRow[];
	/** Which axis the table lists: personas of one segment, or segments of one persona. */
	axis: "persona" | "segment";
	options: ScoringOption[];
	onToggleLink: (segment: SegmentView, persona: PersonaView, linked: boolean) => void;
	/** Extra column between the name and the scoring (the segment group on the persona page). */
	renderMeta?: (row: PersonaSegmentRow) => ReactNode;
}

const GRID = "grid items-center gap-2.5 px-3 py-1.5";

/**
 * The "personas in this segment" / "segments this persona exists in" table
 * (PREC-4055): a link toggle, the name, the scoring picker for that cell, and
 * an edit shortcut once a scoring is set. Same component read from either side.
 */
export const PersonaSegmentTable: FunctionComponent<PersonaSegmentTableProps> = ({
	rows,
	axis,
	options,
	onToggleLink,
	renderMeta,
	...cellActions
}) => {
	const { t } = useTranslation();
	const columns = renderMeta
		? "grid-cols-[32px_300px_92px_260px_auto]"
		: "grid-cols-[32px_320px_260px_auto]";

	return (
		<div
			className="overflow-hidden rounded-lg border border-border-light"
			data-testid={`persona-segment-table-${axis}`}
		>
			<div
				className={classNames(
					GRID,
					columns,
					"bg-background-light text-[10px] font-bold uppercase tracking-wider text-text-disabled"
				)}
			>
				<span />
				<span>
					{t(
						axis === "persona"
							? "leadScoring.detail.personaColumn"
							: "leadScoring.detail.segmentColumn"
					)}
				</span>
				{renderMeta && <span>{t("leadScoring.detail.typeColumn")}</span>}
				<span>{t("leadScoring.stepper.scoring")}</span>
				<span />
			</div>
			{rows.map((row) => {
				const label = axis === "persona" ? row.persona.name : row.segment.name;

				return (
					<div
						key={axis === "persona" ? row.persona.id : row.segment.key}
						className={classNames(
							GRID,
							columns,
							"border-t border-border-light hover:bg-selected-item"
						)}
						data-testid={`link-row-${row.segment.key}-${row.persona.id}`}
					>
						<Toggle
							isChecked={row.linked}
							testId={`link-toggle-${row.segment.key}-${row.persona.id}`}
							onChange={() => onToggleLink(row.segment, row.persona, !row.linked)}
						/>
						<span
							className={classNames(
								"truncate text-xs font-medium",
								row.linked ? "text-text" : "text-text-disabled"
							)}
						>
							{label || t("leadScoring.shell.untitled")}
						</span>
						{renderMeta && (
							<span className="text-[11px] text-text-disabled">{renderMeta(row)}</span>
						)}
						<MatrixCell
							ambiguous={false}
							assignment={row.assignment}
							flash={false}
							linked={row.linked}
							options={options}
							persona={row.persona}
							segment={row.segment}
							onFlashDone={() => undefined}
							{...cellActions}
						/>
						{row.linked && row.assignment ? (
							<button
								className="flex h-6 cursor-pointer items-center gap-1 rounded-md px-2 text-[11px] font-medium text-text-meta opacity-0 hover:bg-background-light hover:text-text group-hover:opacity-100 [.group:hover_&]:opacity-100"
								type="button"
								onClick={() => cellActions.onEdit(row.assignment!.criteriaId)}
							>
								<Icon className="h-3 w-3" icon={faArrowUpRightFromSquare} />
								{t("leadScoring.matrix.editScoringShort")}
							</button>
						) : (
							<span />
						)}
					</div>
				);
			})}
		</div>
	);
};
